import moment from "moment";
import {
  ImportedInvoice,
  ImportedInvoiceEntry,
  ImportedInvoiceEntryMetadata,
  ImportedInvoiceEntryMetadataMap,
  ImportedInvoiceMetadata,
  ImportedInvoiceMetadataMap,
} from "types/importedInvoices";
import {
  formatNumberForSearch,
  handleAxiosError,
  prepareNotificationObject,
} from "utils/utils";
import { matchesSearchTerm, normalizeSearchTerm } from "utils/searchMatch";
import {
  countImportedInvoiceEntries,
  isEntryMatched,
} from "pages/ImportedInvoices/importedInvoiceUtils";
import * as API from "../api/api";
import { StoreState } from "./ImportedInvoicesStore";
import { setNotificationMessage } from "./AppStoreActions";

export const setStoreValue =
  (name: keyof StoreState, value) =>
  ({ setState }) => {
    setState({
      [name]: value,
    });
  };

export const setStoreValues =
  (values) =>
  ({ setState }) => {
    setState({
      ...values,
    });
  };

// Row metadata carries the user's screen state (selection, search visibility); it is
// rebuilt from the previous value so a write never collapses the selection.
const buildInvoiceMetadata = (
  invoice: ImportedInvoice,
  previous?: ImportedInvoiceMetadata
): ImportedInvoiceMetadata => ({
  selected: previous?.selected ?? false,
  visible: previous?.visible ?? true,
  deleted: invoice.isDeleted,
  inEditMode: false,
  changes: [],
  editable: false,
  oldRow: null,
});

// matched entries are the highlighted (orange) rows
const buildEntryMetadata = (
  entry: ImportedInvoiceEntry,
  previous?: ImportedInvoiceEntryMetadata
): ImportedInvoiceEntryMetadata => ({
  selected: previous?.selected ?? false,
  visible: previous?.visible ?? true,
  matched: isEntryMatched(entry),
  highlighted: isEntryMatched(entry),
  inEditMode: false,
  changes: [],
  editable: false,
  oldRow: null,
});

const noDetailState = {
  selectedImportedInvoiceId: null,
  importedInvoiceEntries: [],
  importedInvoiceEntryMetadataMap: {},
};

const formatDate = (value: string | null | undefined): string =>
  value ? moment(value).format("DD.MM.YYYY") : "";

export const hydrateImportedInvoiceEntries = (
  entries: ImportedInvoiceEntry[]
): ImportedInvoiceEntry[] =>
  (entries ?? [])
    .filter((entry) => entry.isDeleted === false)
    .map((entry) => ({
      ...entry,
      validFromPaymentPlanEntryFormatted: formatDate(
        entry.validFromPaymentPlanEntry
      ),
      paymentDateFormatted: formatDate(entry.paymentDate),
      // the resolved CRM partner wins over the name exported by QTG
      insurerDisplayName: entry.insurerNameResolved ?? entry.insurerName ?? "",
      clientDisplayName: entry.clientNameResolved ?? entry.clientName ?? "",
    }));

export const hydrateImportedInvoices = ({
  importedInvoices,
  t,
}: {
  importedInvoices: ImportedInvoice[];
  t: any;
}): ImportedInvoice[] =>
  (importedInvoices ?? [])
    .filter((invoice) => invoice.isDeleted === false)
    .map((invoice) => {
      const entries = hydrateImportedInvoiceEntries(
        invoice.importedInvoiceEntries
      );
      const counts = countImportedInvoiceEntries(entries);
      return {
        ...invoice,
        importedInvoiceEntries: entries,
        ...counts,
        issuedDateFormatted: formatDate(invoice.issuedDate),
        dueDateFormatted: formatDate(invoice.dueDate),
        paidAtSourceFormatted: formatDate(invoice.paidAtSource),
        importedAtFormatted: formatDate(invoice.importedAt),
        // the bank statement entry that paid the invoice while it could not be cleared: "booking date, amount, payer"
        pendingBankStatementEntryFormatted:
          invoice.pendingBankStatementEntryId !== null &&
          invoice.pendingBankStatementEntryId !== undefined
            ? [
                formatDate(invoice.pendingBankStatementEntryBookingDate),
                formatNumberForSearch(invoice.pendingBankStatementEntryAmount ?? NaN),
                invoice.pendingBankStatementEntryClientName ?? "",
              ]
                .filter((part) => part !== "")
                .join(", ")
            : "",
        payerDisplayName:
          invoice.payerClientInfo?.name ?? invoice.payerName ?? "",
        matchStateLabel: t(
          "pct.importedInvoiceMatchState." + invoice.matchState + ".label"
        ),
      };
    });

// --- query ------------------------------------------------------------------

// A selection that is still part of the new result survives, otherwise the detail is cleared.
export const loadImportedInvoices =
  (payload, onSuccess?) =>
  async ({ setState, getState, dispatch }) => {
    try {
      if (getState().importedInvoicesLoadStatus === "loading") return;
      setState({ importedInvoicesLoadStatus: "loading" });

      const response = await API.importedInvoiceQuery({
        From: getState().fromDateFilter
          ? moment(getState().fromDateFilter).format("YYYY-MM-DD")
          : null,
        To: getState().toDateFilter
          ? moment(getState().toDateFilter).format("YYYY-MM-DD")
          : null,
        MatchStates: getState().matchStateFilter
          ? [getState().matchStateFilter]
          : [],
      });

      const importedInvoices = hydrateImportedInvoices({
        importedInvoices: response.data,
        t: payload.t,
      });

      const selectedImportedInvoiceId = getState().selectedImportedInvoiceId;
      const selected = importedInvoices.find(
        (invoice) => invoice.importedInvoiceId === selectedImportedInvoiceId
      );

      // ticked rows stay ticked when they are part of the new result
      const previousMetadataMap = getState().importedInvoiceMetadataMap;
      const importedInvoiceMetadataMap: ImportedInvoiceMetadataMap = {};
      importedInvoices.forEach((invoice) => {
        importedInvoiceMetadataMap[invoice.importedInvoiceId] =
          buildInvoiceMetadata(
            invoice,
            previousMetadataMap[invoice.importedInvoiceId]
          );
      });

      setState({
        importedInvoices,
        importedInvoiceMetadataMap,
        importedInvoicesSelectAll: false,
        importedInvoicesLoadStatus: "success",
        ...(selected ? {} : noDetailState),
      });
      if (selected) dispatch(selectImportedInvoice(selected.importedInvoiceId));
      onSuccess && onSuccess(response.data);
    } catch (error: any) {
      if (error.code !== "ERR_CANCELED") {
        handleAxiosError(error, (message) => {
          dispatch(
            setNotificationMessage(
              prepareNotificationObject("loadImportedInvoices", message)
            )
          );
        });
        setState({ importedInvoicesLoadStatus: "error" });
      }
    }
  };

// --- selection / detail -------------------------------------------------------

// Detail pane: the entries (already loaded with the invoice) of the given invoice
// (null = none). Does not change which rows are ticked.
export const selectImportedInvoice =
  (importedInvoiceId: number | null) =>
  ({ setState, getState }) => {
    const invoice = getState().importedInvoices.find(
      (el: ImportedInvoice) => el.importedInvoiceId === importedInvoiceId
    );
    const entries: ImportedInvoiceEntry[] = invoice
      ? invoice.importedInvoiceEntries ?? []
      : [];
    const previousEntryMetadataMap = getState().importedInvoiceEntryMetadataMap;
    const importedInvoiceEntryMetadataMap: ImportedInvoiceEntryMetadataMap = {};
    entries.forEach((entry) => {
      importedInvoiceEntryMetadataMap[entry.importedInvoiceEntryId] =
        buildEntryMetadata(
          entry,
          previousEntryMetadataMap[entry.importedInvoiceEntryId]
        );
    });
    setState({
      selectedImportedInvoiceId: invoice ? importedInvoiceId : null,
      importedInvoiceEntries: entries,
      importedInvoiceEntryMetadataMap,
    });
  };

const selectedIdsOf = (metadataMap: ImportedInvoiceMetadataMap): number[] =>
  Object.entries(metadataMap)
    .filter(([, meta]) => meta.selected)
    .map(([id]) => Number(id));

// Tick / untick one row. The detail pane follows the row ticked last; unticking the row
// shown there moves the detail to another ticked row, or clears it.
export const toggleImportedInvoiceSelection =
  (importedInvoiceId: number, checked: boolean) =>
  ({ setState, getState, dispatch }) => {
    const previousMetadataMap: ImportedInvoiceMetadataMap =
      getState().importedInvoiceMetadataMap;
    const importedInvoiceMetadataMap: ImportedInvoiceMetadataMap = {
      ...previousMetadataMap,
      [importedInvoiceId]: {
        ...previousMetadataMap[importedInvoiceId],
        selected: checked,
      },
    };
    const selectedIds = selectedIdsOf(importedInvoiceMetadataMap);
    setState({
      importedInvoiceMetadataMap,
      importedInvoicesSelectAll:
        selectedIds.length > 0 &&
        Object.values(importedInvoiceMetadataMap).every(
          (meta) => meta.selected || meta.visible === false
        ),
    });
    if (checked) {
      dispatch(selectImportedInvoice(importedInvoiceId));
    } else if (getState().selectedImportedInvoiceId === importedInvoiceId) {
      dispatch(
        selectImportedInvoice(
          selectedIds.length > 0 ? selectedIds[selectedIds.length - 1] : null
        )
      );
    }
  };

// Header checkbox: ticks / unticks every row visible with the current search term.
export const selectAllImportedInvoices =
  (checked: boolean) =>
  ({ setState, getState, dispatch }) => {
    const previousMetadataMap: ImportedInvoiceMetadataMap =
      getState().importedInvoiceMetadataMap;
    const importedInvoiceMetadataMap: ImportedInvoiceMetadataMap = {};
    Object.entries(previousMetadataMap).forEach(([id, meta]) => {
      importedInvoiceMetadataMap[id] = {
        ...meta,
        selected: meta.visible === false ? meta.selected : checked,
      };
    });
    setState({ importedInvoiceMetadataMap, importedInvoicesSelectAll: checked });
    const selectedIds = selectedIdsOf(importedInvoiceMetadataMap);
    const detailId = getState().selectedImportedInvoiceId;
    if (detailId === null || !selectedIds.includes(detailId)) {
      dispatch(
        selectImportedInvoice(
          selectedIds.length > 0 ? selectedIds[selectedIds.length - 1] : null
        )
      );
    }
  };

// --- writes -------------------------------------------------------------------

export const importImportedInvoices =
  (payload, onSuccess?, onError?) =>
  async ({ setState, dispatch }) => {
    setState({ importImportedInvoicesStatus: "loading" });
    try {
      const response = await API.importImportedInvoices({
        name: payload.name,
        base64String: payload.base64String,
      });
      setState({
        importImportedInvoicesStatus: "success",
        lastImportResult: response.data,
      });
      onSuccess && onSuccess(response.data);
    } catch (error: any) {
      handleAxiosError(error, (message) => {
        dispatch(
          setNotificationMessage(
            prepareNotificationObject("importImportedInvoices", message)
          )
        );
      });
      setState({ importImportedInvoicesStatus: "error" });
      onError && onError();
    }
  };

// pull of the QTG feed for an issue-date window; answers with the same report as the upload
// (plus the window and row count)
export const pullImportedInvoices =
  (payload: { issuedFrom: string; issuedTo: string }, onSuccess?, onError?) =>
  async ({ setState, dispatch }) => {
    setState({ pullImportedInvoicesStatus: "loading" });
    try {
      const response = await API.pullImportedInvoices({
        issuedFrom: payload.issuedFrom,
        issuedTo: payload.issuedTo,
      });
      setState({
        pullImportedInvoicesStatus: "success",
        lastPullResult: response.data,
      });
      onSuccess && onSuccess(response.data);
    } catch (error: any) {
      handleAxiosError(error, (message) => {
        dispatch(
          setNotificationMessage(
            prepareNotificationObject("pullImportedInvoices", message)
          )
        );
      });
      setState({ pullImportedInvoicesStatus: "error" });
      onError && onError();
    }
  };

export const rematchImportedInvoices =
  (payload, onSuccess?, onError?) =>
  async ({ setState, dispatch }) => {
    setState({ rematchImportedInvoicesStatus: "loading" });
    try {
      const response = await API.rematchImportedInvoices({
        importedInvoiceIds: payload.importedInvoiceIds ?? null,
      });
      setState({ rematchImportedInvoicesStatus: "success" });
      onSuccess && onSuccess(response.data);
    } catch (error: any) {
      handleAxiosError(error, (message) => {
        dispatch(
          setNotificationMessage(
            prepareNotificationObject("rematchImportedInvoices", message)
          )
        );
      });
      setState({ rematchImportedInvoicesStatus: "error" });
      onError && onError();
    }
  };

// one request for one or several invoices; answers with the deleted invoices
export const deleteImportedInvoices =
  (payload: { importedInvoiceIds: number[] }, onSuccess?, onError?) =>
  async ({ setState, dispatch }) => {
    setState({ deleteImportedInvoiceStatus: "loading" });
    try {
      const response = await API.deleteImportedInvoices({
        importedInvoiceIds: payload.importedInvoiceIds,
      });
      setState({ deleteImportedInvoiceStatus: "success" });
      onSuccess && onSuccess(response.data);
    } catch (error: any) {
      handleAxiosError(error, (message) => {
        dispatch(
          setNotificationMessage(
            prepareNotificationObject("deleteImportedInvoice", message)
          )
        );
      });
      setState({ deleteImportedInvoiceStatus: "error" });
      onError && onError();
    }
  };

// --- targeted updates after a write -----------------------------------------
// Import and re-match answer with the affected invoices (with entries) in the shape of
// the query, so the rows are patched in place; invoices not yet on screen are added.

export const applyImportedInvoices =
  (payload: { importedInvoices: ImportedInvoice[]; t: any }) =>
  ({ setState, getState, dispatch }) => {
    const hydrated = hydrateImportedInvoices({
      importedInvoices: payload.importedInvoices ?? [],
      t: payload.t,
    });
    if (hydrated.length === 0) return;
    const byId = new Map(hydrated.map((inv) => [inv.importedInvoiceId, inv]));
    const existingIds = new Set(
      getState().importedInvoices.map((el: ImportedInvoice) => el.importedInvoiceId)
    );
    const importedInvoices = [
      ...getState().importedInvoices.map((el: ImportedInvoice) =>
        byId.get(el.importedInvoiceId) ?? el
      ),
      ...hydrated.filter((inv) => !existingIds.has(inv.importedInvoiceId)),
    ];
    const importedInvoiceMetadataMap = { ...getState().importedInvoiceMetadataMap };
    hydrated.forEach((inv) => {
      importedInvoiceMetadataMap[inv.importedInvoiceId] = buildInvoiceMetadata(
        inv,
        importedInvoiceMetadataMap[inv.importedInvoiceId]
      );
    });
    setState({ importedInvoices, importedInvoiceMetadataMap });
    const selectedId = getState().selectedImportedInvoiceId;
    if (selectedId !== null && byId.has(selectedId)) {
      dispatch(selectImportedInvoice(selectedId));
    }
  };

export const removeImportedInvoices =
  (importedInvoiceIds: number[]) =>
  ({ setState, getState }) => {
    const removed = new Set(importedInvoiceIds);
    const importedInvoiceMetadataMap = { ...getState().importedInvoiceMetadataMap };
    importedInvoiceIds.forEach((id) => delete importedInvoiceMetadataMap[id]);
    const detailId = getState().selectedImportedInvoiceId;
    setState({
      importedInvoices: getState().importedInvoices.filter(
        (invoice: ImportedInvoice) => !removed.has(invoice.importedInvoiceId)
      ),
      importedInvoiceMetadataMap,
      importedInvoicesSelectAll: false,
      ...(detailId !== null && removed.has(detailId) ? noDetailState : {}),
    });
  };

// --- client-side search ---------------------------------------------------------

// master grid: number, QTG id, payer, VINs and clients of the entries, match state
export const filterImportedInvoices =
  (payload) =>
  ({ setState, getState, dispatch }) => {
    const term = normalizeSearchTerm(payload.searchTerm);
    const importedInvoiceMetadataMap: ImportedInvoiceMetadataMap = {
      ...getState().importedInvoiceMetadataMap,
    };
    let selectedHidden = false;

    getState().importedInvoices.forEach((invoice: ImportedInvoice) => {
      const meta = importedInvoiceMetadataMap[invoice.importedInvoiceId];
      if (!meta) return;
      const visible = matchesSearchTerm(
        [
          invoice.invoiceNumber,
          invoice.externalId,
          invoice.payerName,
          invoice.payerCode,
          invoice.payerDisplayName,
          invoice.matchStateLabel,
          invoice.pendingBankStatementEntryFormatted,
          ...(invoice.importedInvoiceEntries ?? []).flatMap((e) => [
            e.vin,
            e.clientName,
            e.clientCode,
            e.insurerName,
            e.policyNumberPaymentPlanEntry,
          ]),
        ],
        term
      );
      importedInvoiceMetadataMap[invoice.importedInvoiceId] = { ...meta, visible };
      if (!visible && invoice.importedInvoiceId === getState().selectedImportedInvoiceId) {
        selectedHidden = true;
      }
    });

    setState({ importedInvoiceMetadataMap });
    if (selectedHidden) dispatch(selectImportedInvoice(null));
  };

// detail grid: VIN, insurer, client, policy number of the matched installment
export const filterImportedInvoiceEntries =
  (payload: { entriesSearchTerm: string }) =>
  ({ setState, getState }) => {
    const term = normalizeSearchTerm(payload.entriesSearchTerm);
    const importedInvoiceEntryMetadataMap: ImportedInvoiceEntryMetadataMap = {
      ...getState().importedInvoiceEntryMetadataMap,
    };
    getState().importedInvoiceEntries.forEach((entry: ImportedInvoiceEntry) => {
      const meta = importedInvoiceEntryMetadataMap[entry.importedInvoiceEntryId];
      if (!meta) return;
      const visible =
        entry.isDeleted === false &&
        matchesSearchTerm(
          [
            entry.vin,
            entry.insurerName,
            entry.insurerDisplayName,
            entry.clientName,
            entry.clientCode,
            entry.clientDisplayName,
            entry.policyNumberPaymentPlanEntry,
            entry.offerId,
          ],
          term
        );
      importedInvoiceEntryMetadataMap[entry.importedInvoiceEntryId] = { ...meta, visible };
    });
    setState({ importedInvoiceEntryMetadataMap });
  };
